import type React from 'react';

import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

import { LOCAL_SECTION_PETITION_COUNT } from '#src/app/pages/homepage/shared/api';
import { useCountryCode } from '#src/app/shared/hooks/l10n';

import { ContributeSection } from '../ContributeSection';
import { SeeAllTopicsButton } from '../TrendingSection/components/SeeAllTopicsButton';
import { TrendingSectionHeader } from '../TrendingSection/components/TrendingSectionHeader';
import { useTrackAllTopicsClick } from '../TrendingSection/hooks/useTrackAllTopicsClick';

const Bone: React.FC<{ className: string }> = ({ className }) => (
	<div aria-hidden="true" className={cn('bg-fill-weaker animate-pulse rounded-md', className)} />
);

type CardGridBonesProps = {
	cardCount: number;
	/** Grid used for the cards — should match the section's own grid. */
	gridClassName?: string;
	/** Sizing classes for each card — should match the section's own cards. */
	cardClassName?: string;
};

const CardGridBones: React.FC<CardGridBonesProps> = ({
	cardCount,
	cardClassName = 'h-[288px]',
	gridClassName = 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
}) => (
	<div className={cn('grid gap-6', gridClassName)}>
		{Array.from({ length: cardCount }, (_, i) => (
			<Bone key={i} className={cn(cardClassName, 'w-full rounded-[12px]')} />
		))}
	</div>
);

const TopicChipsBones: React.FC = () => (
	<div className="flex flex-wrap items-center gap-2">
		{[0, 1, 2, 3, 4, 5].map((i) => (
			<Bone key={i} className="h-11 w-24 rounded-md" />
		))}
	</div>
);

type PetitionCardsSectionSkeletonProps = {
	/** Screen-reader-only loading message, e.g. `<Translate value="..." />`. */
	loadingLabel: React.ReactNode;
} & CardGridBonesProps;

/**
 * Shared shape for the homepage sections that render a heading above a grid of
 * petition cards, so their loading states stay consistent with each other.
 */
export const PetitionCardsSectionSkeleton: React.FC<PetitionCardsSectionSkeletonProps> = ({
	loadingLabel,
	...cardGridProps
}) => (
	<section aria-busy="true" className="flex flex-col gap-6">
		{/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review */}
		<span role="status" className="sr-only">
			{loadingLabel}
		</span>
		<div className="flex flex-col gap-4">
			<Bone className="h-12 w-1/2" />
			<Bone className="h-6 w-1/4" />
		</div>
		<CardGridBones {...cardGridProps} />
	</section>
);

/**
 * Unlike the other skeletons, this renders the section's real header and
 * see-all buttons — they need no fetched data, and the button gives fast
 * keyboard tabbing a stable focus stop while the lazy chunk/data resolve.
 * Layout mirrors `TrendingSection` so nothing shifts when it swaps in.
 */
export const TrendingSectionSkeleton: React.FC = () => {
	const trackAllTopicsClick = useTrackAllTopicsClick();

	return (
		<section
			className="text-typography-strong grid gap-6 md:grid-cols-[1fr_auto]"
			aria-labelledby="trending-section-title"
		>
			<TrendingSectionHeader />

			<div className="hidden md:block md:self-center">
				<SeeAllTopicsButton device="desktop" onClick={trackAllTopicsClick} />
			</div>

			<div aria-busy="true" className="flex flex-col gap-6 md:col-span-2">
				{/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review */}
				<span role="status" className="sr-only">
					<Translate value="corgi.homepage.trending_section.loading" />
				</span>
				<TopicChipsBones />
				<CardGridBones cardCount={4} />
			</div>

			<div className="md:hidden">
				<SeeAllTopicsButton device="mobile" onClick={trackAllTopicsClick} />
			</div>
		</section>
	);
};

export const LocalSectionSkeleton: React.FC = () => (
	<PetitionCardsSectionSkeleton
		cardCount={LOCAL_SECTION_PETITION_COUNT}
		gridClassName="justify-items-center md:grid-cols-3"
		cardClassName="h-[338px] max-w-[350px] md:max-w-[390px]"
		loadingLabel={<Translate value="corgi.homepage.local_section.loading" />}
	/>
);

export const HomepageSkeleton: React.FC = () => {
	// the local section is US-only, so don't reserve space for it anywhere else
	const countryCode = useCountryCode();

	return (
		<div aria-busy="true">
			{/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review */}
			<span role="status" className="sr-only">
				<Translate value="corgi.homepage.loading" />
			</span>
			<section className="relative min-h-[320px] md:min-h-[480px]">
				<div className="container pt-6 md:pt-11 lg:pt-15 lg:pb-11">
					<div className="mt-3 space-y-6">
						<Bone className="h-12 w-2/3 md:w-1/2" />
						<Bone className="h-6 w-3/4 md:w-2/5" />
						<Bone className="h-10 w-44 rounded-full" />
					</div>
				</div>
			</section>

			<div className="bg-background-raised">
				<div className="container space-y-8 py-16">
					<Bone className="mx-auto h-8 w-1/3" />
					<div className="grid gap-6 md:grid-cols-3">
						{[0, 1, 2].map((i) => (
							<div key={i} className="flex flex-col items-center gap-4">
								<Bone className="size-16 rounded-full" />
								<Bone className="h-5 w-3/4" />
								<Bone className="h-4 w-full" />
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="container space-y-16 pt-16 pb-6 md:space-y-28 md:pt-28 md:pb-24">
				{countryCode === 'US' && <LocalSectionSkeleton />}
				<TrendingSectionSkeleton />
				<ContributeSection />
			</div>
		</div>
	);
};
